import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductGroupDto, ProductGroupTypeDto } from '../../../../generated-model/models';
import { formatRecordedAt, resolveRecordedBy } from '../../../shared/commons/audit-display.util';
import { compareByNumericValue } from '../../../shared/commons/select-compare.util';
import { ProductGroupTypeService } from '../product-group-type/product-group-type-service';

@Component({
  selector: 'app-product-group-modal',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './product-group-modal.html'
})
export class ProductGroupModal implements OnInit {
  @Input() item: ProductGroupDto | undefined;
  @Input() viewOnly = false;
  @Input() onSave?: (payload: any, isEditMode: boolean) => Promise<boolean>;

  form: FormGroup;
  isEditMode = false;
  submitted = false;
  isSubmitting = false;
  recordedByDisplay = '-';
  recordedAtDisplay = '-';
  productGroupTypeOptions: ProductGroupTypeDto[] = [];
  isProductGroupTypeLoading = false;
  readonly compareByNumericValue = compareByNumericValue;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private productGroupTypeService: ProductGroupTypeService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      id: [null],
      title: ['', [Validators.required, Validators.maxLength(255)]],
      productGroupTypeId: [null, [Validators.required]],
      ordering: [0, [Validators.max(32767)]],
      state: [1]
    });
  }

  ngOnInit(): void {
    if (this.item) {
      this.isEditMode = true;
      const selectedProductGroupTypeId = this.resolveProductGroupTypeId(this.item);
      this.form.patchValue({
        ...this.item,
        productGroupTypeId: selectedProductGroupTypeId
      });
      this.recordedByDisplay = resolveRecordedBy(this.item);
      this.recordedAtDisplay = formatRecordedAt(this.item);
    } else {
      this.form.reset({ ordering: 0, state: 1 });
      this.recordedByDisplay = '-';
      this.recordedAtDisplay = '-';
    }

    if (this.viewOnly) {
      this.form.disable();
    }

    this.loadProductGroupTypes();
  }

  save(): void {
    if (this.viewOnly) {
      return;
    }
    this.submitted = true;
    if (this.form.invalid) return;
    void this.submitAndClose(this.form.value);
  }

  private async submitAndClose(payload: any): Promise<void> {
    if (!this.onSave) {
      this.activeModal.close(payload);
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const isSuccess = await this.onSave(payload, this.isEditMode);
      if (isSuccess) {
        this.activeModal.close(payload);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private loadProductGroupTypes(): void {
    this.isProductGroupTypeLoading = true;
    this.productGroupTypeService.getAllProductGroupTypes(0, 1000, '').subscribe({
      next: (page) => {
        this.productGroupTypeOptions = [...page.content].sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0));
        const currentTypeId = this.toNumberOrNull(this.form.get('productGroupTypeId')?.value)
          ?? this.resolveProductGroupTypeId(this.item);
        this.form.get('productGroupTypeId')?.setValue(currentTypeId, { emitEvent: false });
        this.isProductGroupTypeLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.productGroupTypeOptions = [];
        this.isProductGroupTypeLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private resolveProductGroupTypeId(item?: ProductGroupDto): number | null {
    if (!item) {
      return null;
    }
    const raw =
      (item as any).productGroupTypeId
      ?? (item as any).productGroupType?.id
      ?? (item as any).productGroupTypeid;
    return this.toNumberOrNull(raw);
  }
}
